// Загрузки медиа с прогрессом (из форка «Наш.Чат», DLP-6): скачивание вложения «как файл» и оригинал фото
// для просмотрщика. Живёт на уровне App — скачивание переживает смену чата и закрытие просмотрщика.
// Сеть — только через транспорт (rest.downloadBlob с onDownloadProgress, инв.2 app-ui).
//
// Скачивание «как файл» — по среде:
//   • обычный браузер (desktop/Android) — байты с прогрессом и отменой → Blob → object URL →
//     <a download> (сохранение без повторного запроса). Пересмотр DL-3: ≤ 100 МиБ проходят через
//     память JS — ради прогресса и отмены (раньше навигация: в приложении ничего не происходило);
//   • iOS standalone-PWA — байты с прогрессом → File → navigator.share (нативный шит «Сохранить в
//     Файлы»; навигация там открывает QuickLook без «Готово»). Если пока качалось, истёк user-activation
//     (NotAllowedError) — фаза `ready`: плашка «Готово — нажмите, чтобы сохранить», повторный share
//     по новому касанию (раньше — фолбэк в тот самый QuickLook-тупик). Нет share API → навигация;
//   • Safari-вкладка на iPhone/iPad — прежняя навигация `?download=1` (Content-Disposition): blob-
//     скачивание там ненадёжно, прогресс рисует сам Safari.
// Оригинал фото — байты с прогрессом → object URL (LRU на ORIGINALS_MAX, revoke при вытеснении и
// размонтировании): повторное открытие мгновенно. Обновления прогресса троттлятся.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import type { Attachment, AttachmentId } from '@tele-chat/shared-contracts'
import { downloadTimeoutMs } from '@tele-chat/client-transport'
import type { AttachmentTransfer, MediaItemState } from '@tele-chat/design-system'
import { isApplePlatform } from '@tele-chat/pwa-shell'

import { useRest } from '../transport/TransportContext.js'
import { usePwa } from '../pwa/PwaContext.js'
import { useNotifications } from '../notifications/NotificationsContext.js'

/** Сколько оригиналов фото держим в памяти как object URL. */
export const ORIGINALS_MAX = 8
/** Троттлинг перерисовки прогресса: не чаще раза в PROGRESS_MIN_MS, если процент сдвинулся < 1. */
export const PROGRESS_MIN_MS = 250
/** Сколько живёт object URL после клика по <a download> (браузер успевает начать сохранение). */
const ANCHOR_URL_TTL_MS = 60_000

export type SaveMode = 'blob-anchor' | 'share' | 'navigation'

type Rest = ReturnType<typeof useRest>

export interface MediaTransfers {
  /** Скачать «как файл»; в фазе `ready` (iOS) — сохранить уже скачанное (новое касание). */
  download(att: Attachment): void
  /** Отменить идущее скачивание (без тоста — это решение пользователя). */
  cancel(attachmentId: AttachmentId): void
  /** Состояние скачивания для плашки/плитки/просмотрщика; undefined — не качается. */
  transferOf(attachmentId: AttachmentId): AttachmentTransfer | undefined
  /** Начать (если ещё нет) загрузку оригинала фото для просмотрщика. */
  loadOriginal(att: Attachment): void
  /** Состояние оригинала (loading/ready/error); undefined — не запрашивался. */
  originalOf(attachmentId: AttachmentId): MediaItemState | undefined
  /** Повторить после ошибки. */
  retryOriginal(att: Attachment): void
  /** Просмотрщик ушёл с фото: недокачанный оригинал отменяется (готовый остаётся в кеше). */
  releaseOriginal(attachmentId: AttachmentId): void
}

const MediaTransfersContext = createContext<MediaTransfers | null>(null)

export function useMediaTransfers(): MediaTransfers {
  const v = useContext(MediaTransfersContext)
  if (v === null) throw new Error('useMediaTransfers(): компонент должен быть внутри <MediaTransfersProvider>')
  return v
}

/** Режим сохранения файла для текущей среды (фича-детект платформы, без UA-парса). */
export function saveModeFor(isStandalone: boolean): SaveMode {
  if (typeof navigator === 'undefined') return 'blob-anchor'
  const apple = isApplePlatform({
    platform: navigator.platform ?? '',
    maxTouchPoints: navigator.maxTouchPoints ?? 0,
    hasMSStream: typeof window !== 'undefined' && 'MSStream' in window,
  })
  if (!apple) return 'blob-anchor'
  return isStandalone ? 'share' : 'navigation'
}

interface TransferEntry {
  view: AttachmentTransfer
  controller?: AbortController
  /** iOS: готовый файл, ждущий касания «Сохранить». */
  file?: File
}

interface OriginalEntry {
  state: MediaItemState
  controller?: AbortController
}

export interface MediaTransfersProviderProps {
  children: ReactNode
  /** Режим сохранения файла (инъекция для тестов/диагностики); по умолчанию — по платформе и PWA. */
  resolveSaveMode?: () => SaveMode
}

export function MediaTransfersProvider({ children, resolveSaveMode }: MediaTransfersProviderProps): JSX.Element {
  const rest = useRest()
  const { isStandalone } = usePwa()
  const { notify } = useNotifications()

  const transfers = useRef(new Map<string, TransferEntry>())
  const originals = useRef(new Map<string, OriginalEntry>())
  /** Порядок использования оригиналов для LRU (последний — самый свежий). */
  const lru = useRef<string[]>([])
  /** Версия состояния: растёт на каждое изменение — новый value контекста перерисовывает потребителей. */
  const [version, setVersion] = useState(0)
  const bump = useCallback(() => setVersion((v) => v + 1), [])
  const alive = useRef(true)

  // Размонтирование: оборвать загрузки, освободить object URL.
  useEffect(() => {
    alive.current = true
    const t = transfers.current
    const o = originals.current
    return () => {
      alive.current = false
      for (const e of t.values()) e.controller?.abort()
      for (const e of o.values()) {
        e.controller?.abort()
        if (e.state.status === 'ready') URL.revokeObjectURL(e.state.src)
      }
      t.clear()
      o.clear()
    }
  }, [])

  const failDownload = useCallback(
    (att: Attachment) => notify({ kind: 'error', title: `Не удалось скачать «${att.filename}»` }),
    [notify],
  )

  const setTransfer = useCallback(
    (id: string, entry: TransferEntry | null) => {
      if (entry === null) transfers.current.delete(id)
      else transfers.current.set(id, entry)
      if (alive.current) bump()
    },
    [bump],
  )

  /** iOS: share файла. needs-gesture — user-activation истёк; unsupported — share тут не работает. */
  const tryShare = useCallback(async (file: File): Promise<'shared' | 'cancelled' | 'needs-gesture' | 'unsupported'> => {
    const nav = navigator as {
      share?: (d: { files: File[]; title?: string }) => Promise<void>
      canShare?: (d: { files: File[] }) => boolean
    }
    if (typeof nav.share !== 'function' || typeof nav.canShare !== 'function') return 'unsupported'
    if (!nav.canShare({ files: [file] })) return 'unsupported'
    try {
      await nav.share({ files: [file], title: file.name })
      return 'shared'
    } catch (err) {
      const name = err instanceof Error ? err.name : ''
      if (name === 'AbortError') return 'cancelled'
      if (name === 'NotAllowedError') return 'needs-gesture'
      return 'unsupported'
    }
  }, [])

  const shareOrPark = useCallback(
    async (att: Attachment, file: File): Promise<void> => {
      const res = await tryShare(file)
      if (res === 'shared' || res === 'cancelled') {
        setTransfer(att.id, null)
        return
      }
      if (res === 'needs-gesture') {
        setTransfer(att.id, { view: { phase: 'ready' }, file })
        notify({ kind: 'info', title: `«${att.filename}» загружен — нажмите «Сохранить»` })
        return
      }
      // share недоступен — прежняя навигация (Content-Disposition), байты перекачаются браузером.
      setTransfer(att.id, null)
      await navigationDownload(rest, att)
    },
    [tryShare, setTransfer, notify, rest],
  )

  const download = useCallback(
    (att: Attachment): void => {
      const existing = transfers.current.get(att.id)
      if (existing?.view.phase === 'downloading') return
      void (async () => {
        try {
          if (existing?.view.phase === 'ready' && existing.file !== undefined) {
            await shareOrPark(att, existing.file)
            return
          }
          const mode = resolveSaveMode !== undefined ? resolveSaveMode() : saveModeFor(isStandalone)
          if (mode === 'navigation') {
            // Safari-вкладка iOS: браузер качает сам и рисует прогресс; у нас — только preflight.
            setTransfer(att.id, { view: { phase: 'downloading' } })
            try {
              await navigationDownload(rest, att)
            } finally {
              setTransfer(att.id, null)
            }
            return
          }

          const controller = new AbortController()
          // До первых байтов (ожидание ответа сервера) — «бегущая» полоса без цифр: честнее, чем «0% · 0 Б».
          setTransfer(att.id, { view: { phase: 'downloading' }, controller })
          const report = throttledProgress((loaded, total) => {
            const cur = transfers.current.get(att.id)
            if (cur?.controller !== controller) return
            setTransfer(att.id, {
              view: { phase: 'downloading', loadedBytes: loaded, totalBytes: total, ...progressOf(loaded, total) },
              controller,
            })
          })
          let blob: Blob
          try {
            blob = await rest.downloadBlob(att.id, {
              signal: controller.signal,
              timeoutMs: downloadTimeoutMs(att.size),
              onDownloadProgress: (loaded, total) => report(loaded, total ?? att.size),
            })
          } catch (err) {
            if (controller.signal.aborted) {
              setTransfer(att.id, null) // отмена пользователем — без тоста
              return
            }
            throw err
          }
          if (mode === 'share') {
            await shareOrPark(att, new File([blob], att.filename, { type: att.mime }))
            return
          }
          saveViaAnchor(blob, att.filename)
          setTransfer(att.id, null)
        } catch {
          setTransfer(att.id, null)
          failDownload(att)
        }
      })()
    },
    [isStandalone, resolveSaveMode, rest, setTransfer, shareOrPark, failDownload],
  )

  const cancel = useCallback(
    (attachmentId: AttachmentId): void => {
      const e = transfers.current.get(attachmentId)
      if (e === undefined) return
      e.controller?.abort()
      setTransfer(attachmentId, null)
    },
    [setTransfer],
  )

  const transferOf = useCallback(
    (attachmentId: AttachmentId): AttachmentTransfer | undefined => transfers.current.get(attachmentId)?.view,
    [],
  )

  // ── оригиналы фото ──────────────────────────────────────────────────────────────────────────

  const touch = useCallback((id: string) => {
    lru.current = [...lru.current.filter((x) => x !== id), id]
    // вытесняем старейшие по использованию: готовые — revoke object URL, недокачанные — обрыв
    while (lru.current.length > ORIGINALS_MAX) {
      const victim = lru.current.shift()
      if (victim === undefined) break
      const e = originals.current.get(victim)
      if (e?.state.status === 'ready') URL.revokeObjectURL(e.state.src)
      e?.controller?.abort()
      originals.current.delete(victim)
    }
  }, [])

  const setOriginal = useCallback(
    (id: string, entry: OriginalEntry | null) => {
      if (entry === null) originals.current.delete(id)
      else originals.current.set(id, entry)
      if (alive.current) bump()
    },
    [bump],
  )

  const loadOriginal = useCallback(
    (att: Attachment): void => {
      const existing = originals.current.get(att.id)
      if (existing !== undefined && existing.state.status !== 'error') {
        touch(att.id)
        return
      }
      const controller = new AbortController()
      setOriginal(att.id, { state: { status: 'loading' }, controller })
      touch(att.id)
      const report = throttledProgress((loaded, total) => {
        if (originals.current.get(att.id)?.controller !== controller) return
        setOriginal(att.id, {
          state: { status: 'loading', loadedBytes: loaded, totalBytes: total, ...progressOf(loaded, total) },
          controller,
        })
      })
      void rest
        .downloadBlob(att.id, {
          signal: controller.signal,
          timeoutMs: downloadTimeoutMs(att.size),
          onDownloadProgress: (loaded, total) => report(loaded, total ?? att.size),
        })
        .then((blob) => {
          if (originals.current.get(att.id)?.controller !== controller) return
          setOriginal(att.id, { state: { status: 'ready', src: URL.createObjectURL(blob) } })
        })
        .catch(() => {
          if (originals.current.get(att.id)?.controller !== controller) return
          if (controller.signal.aborted) setOriginal(att.id, null)
          else setOriginal(att.id, { state: { status: 'error' } })
        })
    },
    [rest, setOriginal, touch],
  )

  const originalOf = useCallback(
    (attachmentId: AttachmentId): MediaItemState | undefined => originals.current.get(attachmentId)?.state,
    [],
  )

  const retryOriginal = useCallback(
    (att: Attachment): void => {
      const e = originals.current.get(att.id)
      if (e?.state.status === 'error') originals.current.delete(att.id)
      loadOriginal(att)
    },
    [loadOriginal],
  )

  const releaseOriginal = useCallback(
    (attachmentId: AttachmentId): void => {
      const e = originals.current.get(attachmentId)
      if (e?.state.status !== 'loading') return
      e.controller?.abort()
      lru.current = lru.current.filter((x) => x !== attachmentId)
      setOriginal(attachmentId, null)
    },
    [setOriginal],
  )

  // Новый объект value на каждое изменение (version) — потребители перерисовываются и читают свежие
  // transferOf/originalOf (сами функции стабильны и читают ref-ы).
  const value: MediaTransfers = useMemo(
    () => ({ download, cancel, transferOf, loadOriginal, originalOf, retryOriginal, releaseOriginal }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- version: сигнал «состояние в ref-ах изменилось»
    [download, cancel, transferOf, loadOriginal, originalOf, retryOriginal, releaseOriginal, version],
  )

  return <MediaTransfersContext.Provider value={value}>{children}</MediaTransfersContext.Provider>
}

/** Процент из байтов (всего неизвестно → без progress: «бегущая» полоса). */
function progressOf(loaded: number, total: number | undefined): { progress?: number } {
  return total !== undefined && total > 0 ? { progress: Math.min(1, loaded / total) } : {}
}

/**
 * Троттлинг отчётов прогресса: пропускаем, если процент сдвинулся меньше чем на 1 и с прошлой
 * отрисовки прошло < PROGRESS_MIN_MS; первый и финальный (loaded ≥ total) отчёты — всегда.
 */
export function throttledProgress(
  emit: (loaded: number, total: number | undefined) => void,
  now: () => number = () => Date.now(),
): (loaded: number, total: number | undefined) => void {
  let lastAt = Number.NEGATIVE_INFINITY
  let lastPct = -1
  return (loaded, total) => {
    const pct = total !== undefined && total > 0 ? Math.floor((loaded / total) * 100) : -1
    const t = now()
    const first = lastAt === Number.NEGATIVE_INFINITY
    const final = total !== undefined && loaded >= total
    const moved = pct >= 0 && pct - lastPct >= 1
    if (!first && !final && !moved && t - lastAt < PROGRESS_MIN_MS) return
    lastAt = t
    lastPct = pct
    emit(loaded, total)
  }
}

/** Blob → object URL → <a download> (desktop/Android). URL живёт минуту — сохранение успевает начаться. */
export function saveViaAnchor(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), ANCHOR_URL_TTL_MS)
}

/**
 * Навигационное скачивание (Safari-вкладка iOS / нет share): preflight download-url (живость сессии +
 * ACL: протухшая кука → тост, а не сохранённый браузером JSON-ошибки) → <a download href="…?download=1">
 * (Content-Disposition: attachment — iOS Safari уважает только заголовок).
 */
export async function navigationDownload(rest: Rest, att: Attachment): Promise<void> {
  const url = await rest.downloadUrl(att.id)
  const href = `${url}${url.includes('?') ? '&' : '?'}download=1`
  const a = document.createElement('a')
  a.href = href
  a.download = att.filename
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
}
